/**
 * Quotation PDF Generation Utilities
 * Based on invoice PDF utilities but for quotations
 */

import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

/**
 * Generates PDF from quotation component
 * @param {React.RefObject} componentRef - Reference to the quotation component
 * @param {string} template - Template type (default, modern, formal)
 * @param {Object} quotationForm - Quotation data
 * @param {Function} statusCallback - Callback to update generation status
 * @returns {Promise<Blob>} PDF blob
 */
export async function generateQuotationPDF(componentRef, template, quotationForm, statusCallback) {
  try {
    statusCallback?.('Preparing quotation layout...');
    
    // Wait for fonts to be fully loaded
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }
    
    // Add a small delay to ensure all styles are applied
    await new Promise(resolve => setTimeout(resolve, 500));
    
    statusCallback?.('Capturing quotation content...');
    
    // Use html2canvas with optimized settings for better rendering
    const canvas = await html2canvas(componentRef.current, {
      scale: 2, // High quality
      useCORS: true, // Allow cross-origin images
      allowTaint: true, // Allow tainted canvas for external resources
      backgroundColor: '#ffffff',
      logging: false,
      foreignObjectRendering: false, // Disable for better compatibility
      imageTimeout: 15000, // Longer timeout for external resources
      removeContainer: true,
      onclone: function(clonedDoc, element) {
        // Ensure all elements are visible and properly styled
        element.style.transform = 'none';
        element.style.WebkitTransform = 'none';
        
        // Force font loading in the cloned document
        const style = clonedDoc.createElement('style');
        style.textContent = `
          * {
            font-family: "Josefin Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
          }
        `;
        clonedDoc.head.appendChild(style);
        
        return element;
      }
    });
    
    const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
    
    statusCallback?.('Converting to PDF...');
    
    // Create an image element to get dimensions
    const img = new Image();
    const pdfBlob = await new Promise((resolve, reject) => {
      img.onload = () => {
        try {
          // Create PDF with appropriate orientation based on template
          const getOrientation = (template) => {
            switch(template) {
              case 'modern':
              case 'formal':
                return 'portrait';
              case 'default':
              default:
                return 'landscape';
            }
          };
          
          const pdf = new jsPDF({
            orientation: getOrientation(template),
            unit: 'mm',
            format: 'a4'
          });
          
          // Calculate dimensions to fit image properly
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          const margin = 10; // 10mm margin
          const availableWidth = pageWidth - (margin * 2);
          const availableHeight = pageHeight - (margin * 2);
          
          // Calculate scaling to maintain aspect ratio
          const imgAspectRatio = img.width / img.height;
          const pageAspectRatio = availableWidth / availableHeight;
          
          let finalWidth, finalHeight;
          
          if (imgAspectRatio > pageAspectRatio) {
            // Image is wider, scale by width
            finalWidth = availableWidth;
            finalHeight = availableWidth / imgAspectRatio;
          } else {
            // Image is taller, scale by height
            finalHeight = availableHeight;
            finalWidth = availableHeight * imgAspectRatio;
          }
          
          // Center horizontally, position at top with margin
          const x = (pageWidth - finalWidth) / 2;
          const y = margin; // Start from top margin
          
          // Add image to PDF
          pdf.addImage(dataUrl, 'JPEG', x, y, finalWidth, finalHeight);
          
          // Convert to blob
          const pdfBlobResult = pdf.output('blob');
          resolve(pdfBlobResult);
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load captured quotation image'));
      };
      
      img.src = dataUrl;
    });
    
    statusCallback?.('PDF generated successfully');
    return pdfBlob;
    
  } catch (error) {
    console.error('Error generating quotation PDF:', error);
    statusCallback?.('Error generating PDF');
    throw error;
  }
}

/**
 * Generates filename for quotation PDF
 * @param {Object} quotationForm - Quotation data
 * @returns {string} Generated filename
 */
export function generateQuotationPDFFilename(quotationForm) {
  const quotationNo = quotationForm?.quotationNo || 'Quotation';
  const clientName = quotationForm?.clientDetail?.name || 'Client';
  const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
  
  // Clean the strings to be filename-safe
  const cleanQuotationNo = quotationNo.replace(/[^a-zA-Z0-9-_]/g, '_');
  const cleanClientName = clientName.replace(/[^a-zA-Z0-9-_]/g, '_');
  
  return `Quotation_${cleanQuotationNo}_${cleanClientName}_${date}.pdf`;
}

/**
 * Formats quotation data for email attachment
 * @param {Object} quotationForm - Quotation data
 * @param {Object} currentUser - Current user data
 * @returns {Object} Email data formatted for quotation
 */
export function formatQuotationEmailWithAttachment(quotationForm, currentUser) {
  const quotationNo = quotationForm?.quotationNo || 'Quotation';
  const clientName = quotationForm?.clientDetail?.name || 'Valued Client';
  const companyName = quotationForm?.companyDetail?.companyName || 'Our Company';
  const totalAmount = quotationForm?.totalAmount || 0;
  const currencyUnit = quotationForm?.currencyUnit || '₹';
  
  const subject = `Quotation ${quotationNo} from ${companyName}`;
  
  const htmlBody = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
      <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
        <h2 style="color: #333; margin: 0 0 10px 0;">Quotation ${quotationNo}</h2>
        <p style="color: #666; margin: 0;">From ${companyName}</p>
      </div>
      
      <div style="margin-bottom: 20px;">
        <p>Dear ${clientName},</p>
        
        <p>Thank you for your interest in our services. Please find attached the quotation for your requirements.</p>
        
        <div style="background-color: #e3f2fd; padding: 15px; border-radius: 6px; margin: 20px 0;">
          <h3 style="margin: 0 0 10px 0; color: #1976d2;">Quotation Summary</h3>
          <p style="margin: 5px 0;"><strong>Quotation Number:</strong> ${quotationNo}</p>
          <p style="margin: 5px 0;"><strong>Total Amount:</strong> ${currencyUnit}${totalAmount.toLocaleString()}</p>
          <p style="margin: 5px 0;"><strong>Valid Until:</strong> ${new Date(quotationForm?.dueDate || Date.now()).toLocaleDateString()}</p>
        </div>
        
        <p>Key details:</p>
        <ul style="color: #555;">
          <li>Detailed breakdown of all items and services</li>
          <li>Competitive pricing with transparent costs</li>
          <li>Professional quality assurance</li>
          <li>Flexible payment terms available</li>
        </ul>
        
        <p>Please review the attached PDF quotation and feel free to contact us if you have any questions or need clarifications.</p>
        
        <p>We look forward to working with you and appreciate the opportunity to serve your needs.</p>
      </div>
      
      <div style="border-top: 1px solid #eee; padding-top: 20px; margin-top: 30px;">
        <p style="margin: 5px 0;"><strong>Best regards,</strong></p>
        <p style="margin: 5px 0;">${currentUser?.name || 'Sales Team'}</p>
        <p style="margin: 5px 0;">${companyName}</p>
        ${quotationForm?.companyDetail?.companyEmail ? `<p style="margin: 5px 0;">Email: ${quotationForm.companyDetail.companyEmail}</p>` : ''}
        ${quotationForm?.companyDetail?.companyMobile ? `<p style="margin: 5px 0;">Phone: ${quotationForm.companyDetail.companyMobile}</p>` : ''}
      </div>
      
      <div style="background-color: #f5f5f5; padding: 15px; border-radius: 6px; margin-top: 20px;">
        <p style="margin: 0; font-size: 12px; color: #777; text-align: center;">
          This quotation was generated automatically by LogiSlip. 
          Please do not reply directly to this email if it was sent from an automated system.
        </p>
      </div>
    </div>
  `;
  
  return {
    subject,
    htmlBody
  };
}

const quotationPdfUtils = {
  generateQuotationPDF,
  generateQuotationPDFFilename,
  formatQuotationEmailWithAttachment
};

export default quotationPdfUtils;